// themes/getModuleTheme.js
import { createTheme } from "@mui/material/styles";
import { darkColors, lightColors } from "./colors";
import fonts from "./fonts";

export const getModuleTheme = (module, mode) => {
  const base = {
    palette: {
      mode,
    },
  };
  const textColor = mode === "light" ? lightColors.black.full : darkColors.black.full;
  switch (module) {
    case "dashboard":
      return createTheme({
        ...base,
        palette: {
          ...base.palette,
          primary: { main: "#2196f3" },
        },
      });

    case "users":
      return createTheme({
        ...base,
        palette: {
          ...base.palette,
          primary: { main: "#9c27b0" },
        },
      });

    default:
      return createTheme({
        ...base,
        palette: {
          ...base.palette,
          ...(mode === "light"
            ? {
                common: {
                  black: lightColors.black.full,
                  white: lightColors.white.full,
                },
                primary: {
                  main: lightColors.primary.blue,
                  light: lightColors.white.full,
                  dark: lightColors.black.full,
                  contrastText: lightColors.black.full,
                },
                secondary: {
                  main: lightColors.secondary.orange,
                },
                background: {
                  primary: lightColors.white.full,
                  secondary: lightColors.black.light,
                  light: lightColors.white.light40,
                  shade: lightColors.black.light,
                  popup: darkColors.black.full,
                },
                borderColor: lightColors.black.light10,
                text: {
                  primary: lightColors.black.full,
                  secondary: lightColors.black.light40,
                  colortext: lightColors.primary.blue,
                  white: lightColors.white.full,
                  black: lightColors.black.full,
                  textviolet: lightColors.secondary.darkviolet,
                },
                button: {
                  primary: lightColors.primary.blue100,
                  disabled: lightColors.black.light60,
                },
                icon: {
                  light: lightColors.white.full,
                  dark: lightColors.black.full,
                  main: lightColors.black.full,
                },
              }
            : {
                common: {
                  black: darkColors.black.full,
                  white: darkColors.white.full,
                },
                primary: {
                  main: darkColors.primary.blue,
                  light: darkColors.white.full,
                  dark: darkColors.black.full,
                  contrastText: lightColors.black.full,
                },
                secondary: {
                  main: lightColors.primary.blue,
                },
                background: {
                  primary: darkColors.white.full,
                  secondary: darkColors.black.light,
                  light: darkColors.white.light40,
                  shade: darkColors.white.light,
                  popup: lightColors.black.full,
                },
                borderColor: darkColors.black.light10,
                text: {
                  primary: darkColors.black.full,
                  secondary: darkColors.black.light40,
                  colortext: lightColors.primary.blue,
                  white: lightColors.white.full,
                  black: lightColors.black.full,
                  textviolet: darkColors.secondary.darkviolet,
                },
                button: {
                  primary: lightColors.primary.blue100,
                  disabled: lightColors.black.light60,
                },
                icon: {
                  light: darkColors.white.full,
                  dark: darkColors.black.full,
                  main: darkColors.black.full,
                },
              }),
        },
        typography: {
          fontFamily: fonts.families.inter,
          h1: {
            fontSize: fonts.sizes[64].regular.fontSize,
            fontWeight: fonts.sizes[64].regular.fontWeight,
            lineHeight: fonts.sizes[64].regular.lineHeight,
            textAlign: fonts.sizes[64].regular.textAlign,
            fontFamily: fonts.families.inter,
            color: textColor,
          },
          h2: {
            fontSize: fonts.sizes[48].semibold.fontSize,
            fontWeight: fonts.sizes[48].semibold.fontWeight,
            lineHeight: fonts.sizes[48].semibold.lineHeight,
            textAlign: fonts.sizes[48].semibold.textAlign,
            fontFamily: fonts.families.inter,
            color: textColor,
          },
          h3: {
            fontSize: fonts.sizes[24].semibold.fontSize,
            fontWeight: fonts.sizes[24].semibold.fontWeight,
            lineHeight: fonts.sizes[24].semibold.lineHeight,
            textAlign: fonts.sizes[24].semibold.textAlign,
            fontFamily: fonts.families.inter,
            color: textColor,
          },
          h4: {
            fontSize: fonts.sizes[18].semibold.fontSize,
            fontWeight: fonts.sizes[18].semibold.fontWeight,
            lineHeight: fonts.sizes[18].semibold.lineHeight,
            textAlign: fonts.sizes[18].semibold.textAlign,
            fontFamily: fonts.families.inter,
            color: textColor,
          },
          h5: {
            fontSize: fonts.sizes[14].semibold.fontSize,
            fontWeight: fonts.sizes[14].semibold.fontWeight,
            lineHeight: fonts.sizes[14].semibold.lineHeight,
            textAlign: fonts.sizes[14].semibold.textAlign,
            fontFamily: fonts.families.inter,
            color: textColor,
          },
          h6: {
            fontSize: fonts.sizes[12].semibold.fontSize,
            fontWeight: fonts.sizes[12].semibold.fontWeight,
            lineHeight: fonts.sizes[12].semibold.lineHeight,
            textAlign: fonts.sizes[12].semibold.textAlign,
            fontFamily: fonts.families.inter,
            color: textColor,
          },
          body1: {
            fontSize: fonts.sizes[18].regular.fontSize,
            fontWeight: fonts.sizes[18].regular.fontWeight,
            lineHeight: fonts.sizes[18].regular.lineHeight,
            textAlign: fonts.sizes[18].regular.textAlign,
            fontFamily: fonts.families.inter,
            color: textColor,
          },
          body2: {
            fontSize: fonts.sizes[14].regular.fontSize,
            fontWeight: fonts.sizes[14].regular.fontWeight,
            lineHeight: fonts.sizes[14].regular.lineHeight,
            textAlign: fonts.sizes[14].regular.textAlign,
            fontFamily: fonts.families.inter,
            color: textColor,
          },
          body3: {
            fontSize: fonts.sizes[12].regular.fontSize,
            fontWeight: fonts.sizes[12].regular.fontWeight,
            lineHeight: fonts.sizes[12].regular.lineHeight,
            textAlign: fonts.sizes[12].regular.textAlign,
            fontFamily: fonts.families.inter,
            color: textColor,
          },
          subtitle1: {
            fontSize: fonts.sizes[18].semibold.fontSize,
            fontWeight: fonts.sizes[18].semibold.fontWeight,
            lineHeight: fonts.sizes[18].semibold.lineHeight,
            textAlign: fonts.sizes[18].semibold.textAlign,
            fontFamily: fonts.families.inter,
            color: textColor,
          },
          subtitle2: {
            fontSize: fonts.sizes[14].semibold.fontSize,
            fontWeight: fonts.sizes[14].semibold.fontWeight,
            lineHeight: fonts.sizes[14].semibold.lineHeight,
            textAlign: fonts.sizes[14].semibold.textAlign,
            fontFamily: fonts.families.inter,
            color: textColor,
          },
        },
      });
  }
};
